import {ReactNode, useState} from 'react';
import {Avatar, Drawer, IconButton} from '@mui/material';
import {AddRounded, CheckRounded, CloseRounded, DeleteOutlineRounded, ExpandMoreRounded, RemoveRounded} from '@mui/icons-material';
import {ReceiptPortion} from '../../../services/receipt/receiptModel';
import {isGuest, participantKey, participantName, SplitParticipant} from '../../../services/groups/participants';
import {AVATAR_COLORS, avatarSrc, initials} from '../../../utils/avatar';
import {formatCentsAsCurrency, formatCurrency} from '../../../utils/format';
import {lineTotalCents, ReviewRow} from './reviewModel';
import {allocatePortions, normalizeQuantity} from './portionModel';
import classes from './QuantityAssignment.module.scss';
import ReceiptWizardLayout from './ReceiptWizardLayout';

interface Props {
  rows: ReviewRow[];
  participants: SplitParticipant[];
  currency?: string | null;
  currentUserId?: number;
  onChange: (rowId: number, portions: ReceiptPortion[]) => void;
  onBack?: () => void;
  onSave: () => void;
  canSave: boolean;
  saving: boolean;
  busy?: boolean;
  guests: ReactNode;
  errors: ReactNode;
  editingName?: ReactNode;
  saveLabel?: string;
  customHelp?: ReactNode;
  onCustomOpenChange?: (open: boolean) => void;
}

export default function QuantityAssignment({rows, participants, currency, currentUserId, onChange, onBack, onSave, canSave, saving, busy, guests, errors, editingName, saveLabel = 'Save receipt', customHelp, onCustomOpenChange}: Props) {
  const [assignedOpen, setAssignedOpen] = useState(true);
  const [customId, setCustomId] = useState<number>();
  const [quantity, setQuantity] = useState('1');
  const [selected, setSelected] = useState<string[]>([]);
  const [adjusting, setAdjusting] = useState(false);
  const [percentages, setPercentages] = useState<Record<string, string>>({});
  const keys = participants.map(participantKey);
  const outcomes = rows.map((row, index) => allocatePortions(row, index, keys));
  const completeCount = outcomes.filter(outcome => outcome.complete).length;
  const remainingCount = rows.length - completeCount;
  const customIndex = rows.findIndex(row => row.id === customId);
  const customRow = rows[customIndex];
  const remaining = outcomes[customIndex]?.remaining ?? 0;
  const chosenQuantity = Number(quantity.replace(',', '.'));
  const basisPoints = selected.map(key => Math.round(Number((percentages[key] ?? '').replace(',', '.')) * 100));
  const percentageTotal = basisPoints.reduce((sum, weight) => sum + weight, 0);
  const validPercentages = !adjusting || (selected.every((key, index) => Number((percentages[key] ?? '').replace(',', '.')) === basisPoints[index] / 100) && basisPoints.every(weight => Number.isInteger(weight) && weight > 0) && percentageTotal === 10000);
  const quantityValid = Number.isFinite(chosenQuantity) && chosenQuantity > 0 && chosenQuantity <= remaining
    && chosenQuantity === normalizeQuantity(chosenQuantity);
  const candidate: ReceiptPortion = {quantity: chosenQuantity, participantKeys: selected,
    ...(adjusting ? {basisPoints} : {})};
  const previewOutcome = customRow && quantityValid && selected.length >= 2 && validPercentages
    ? allocatePortions({...customRow, allocations: [...(customRow.allocations ?? []), candidate]}, customIndex, keys)
    : undefined;
  const preview = previewOutcome?.valid ? previewOutcome.portions.slice(-1)[0] : undefined;
  const nameForKey = (key: string) => {
    const person = participants.find(participant => participantKey(participant) === key);
    return person ? participantName(person) : 'Unavailable person';
  };
  const locked = saving || busy;

  const avatar = (person: SplitParticipant, index: number) => <Avatar aria-hidden="true"
    src={!isGuest(person) ? avatarSrc(person) : undefined}
    sx={{width: 26, height: 26, fontSize: 10, fontWeight: 700, bgcolor: AVATAR_COLORS[index % AVATAR_COLORS.length]}}>
    {initials(participantName(person))}
  </Avatar>;

  const assignOne = (row: ReviewRow, key: string, available: number) => {
    if (locked || available <= 0) return;
    const amount = Math.min(1, available);
    const portions = [...(row.allocations ?? [])];
    const existing = portions.findIndex(portion => portion.participantKeys.length === 1 && portion.participantKeys[0] === key && !portion.basisPoints);
    if (existing >= 0) portions[existing] = {...portions[existing], quantity: normalizeQuantity(portions[existing].quantity + amount)};
    else portions.push({quantity: amount, participantKeys: [key]});
    onChange(row.id, portions);
  };

  const openCustom = (row: ReviewRow, available: number) => {
    onCustomOpenChange?.(true);
    setCustomId(row.id);
    setQuantity(String(Math.min(1, available)));
    setSelected([]);
    setAdjusting(false);
    setPercentages({});
  };

  const renderItem = (row: ReviewRow, index: number) => {
    const outcome = outcomes[index];
    const groupedPortions = new Map<string, {portion: ReceiptPortion; indexes: number[]}>();
    (row.allocations ?? []).forEach((portion, portionIndex) => {
      const signature = JSON.stringify(portion.participantKeys.map((key, memberIndex) =>
        [key, portion.basisPoints?.[memberIndex] ?? null]).sort(([first], [second]) => String(first).localeCompare(String(second))));
      const existing = groupedPortions.get(signature);
      if (existing) {
        existing.portion.quantity = normalizeQuantity(existing.portion.quantity + portion.quantity);
        existing.indexes.push(portionIndex);
      } else groupedPortions.set(signature, {portion: {...portion}, indexes: [portionIndex]});
    });
    return <article key={row.id} className={`${classes.item} ${outcome.complete ? classes.complete : ''}`} aria-label={row.name}>
      <div className={classes.itemHeading}>
        <span className={`${classes.dot} ${outcome.complete ? classes.green : classes.amber}`}
          role="img" aria-label={outcome.complete ? 'Fully assigned' : 'Needs assignment'}/>
        <div><h3>{row.name}</h3><p>{outcome.complete ? `${row.quantity} ×` : `${Math.max(0, outcome.remaining)} of ${row.quantity} left ·`} {formatCurrency(row.price, currency)} each</p></div>
        <strong>{formatCentsAsCurrency(lineTotalCents(row), currency)}</strong>
      </div>
      {!outcome.complete && <>
        <div className={classes.choices}>
          {participants.map((person, personIndex) => <button type="button" key={participantKey(person)} disabled={locked || outcome.remaining <= 0}
            aria-label={`Assign one ${row.name} to ${participantName(person)}`}
            onClick={() => assignOne(row, participantKey(person), outcome.remaining)}>
            {avatar(person, personIndex)}{participantName(person)}{isGuest(person) && <small>Guest</small>}
          </button>)}
        </div>
        <div className={classes.shortcuts}>
          <button type="button" disabled={locked || !keys.length || outcome.remaining <= 0}
            aria-label={`Share remaining ${row.name} with everyone`}
            onClick={() => onChange(row.id, [...(row.allocations ?? []), {quantity: outcome.remaining, participantKeys: [...keys]}])}>÷ All</button>
          <button type="button" disabled={locked || participants.length < 2 || outcome.remaining <= 0}
            aria-label={`Custom split for ${row.name}`} onClick={() => openCustom(row, outcome.remaining)}>Custom</button>
        </div>
      </>}
      {!!row.allocations?.length && <div className={classes.tags}>
        {Array.from(groupedPortions, ([signature, {portion, indexes}]) => {
          const everyone = !portion.basisPoints && keys.length > 1 && keys.length === portion.participantKeys.length && keys.every(key => portion.participantKeys.includes(key));
          const names = portion.participantKeys.map(key => participants.find(person => participantKey(person) === key))
            .map(person => person ? participantName(person) : 'Unavailable person').join(' + ');
          const label = `${everyone ? '÷ All' : names}${portion.quantity !== 1 || portion.participantKeys.length > 1 ? ` ×${portion.quantity}` : ''}${portion.basisPoints ? ' · Custom %' : ''}`;
          return <button type="button" key={signature} disabled={locked} aria-label={`Remove ${label} from ${row.name}`}
            title={`Remove ${label} from ${row.name}`}
            onClick={() => onChange(row.id, row.allocations!.filter((_, indexToRemove) => !indexes.includes(indexToRemove)))}>
            {label}<DeleteOutlineRounded aria-hidden="true"/>
          </button>;
        })}
        {!outcome.complete && <span>{Math.max(0, outcome.remaining)} left</span>}
      </div>}
    </article>;
  };

  const completedByPerson = new Map<string, {row: ReviewRow; index: number}[]>();
  const sharedCompleted: {row: ReviewRow; index: number}[] = [];
  rows.forEach((row, index) => {
    if (!outcomes[index].complete) return;
    const recipients = Array.from(new Set(row.allocations?.flatMap(portion => portion.participantKeys)));
    if (recipients.length !== 1) {
      sharedCompleted.push({row, index});
      return;
    }
    const entries = completedByPerson.get(recipients[0]) ?? [];
    entries.push({row, index});
    completedByPerson.set(recipients[0], entries);
  });

  return <ReceiptWizardLayout tourStage="split" title="Who had what?" titleId="assignment-title"
    subtitle="Tap a person to assign · Tap a tag to remove"
    onBack={onBack} backLabel="Back to destination" backDisabled={locked}
    contentClassName={classes.board}
    accessory={<span className={completeCount === rows.length ? classes.finishedCount : classes.count}>{completeCount}/{rows.length}</span>}>
    {editingName}
    {outcomes.some(outcome => !outcome.valid) && <p role="alert">Some quantities or assignments are invalid. Remove their tags or return to item review to correct the quantities.</p>}
    {!!remainingCount && <><h2 className={classes.sectionTitle}>Items to assign <span>({remainingCount})</span></h2>
      {rows.map((row, index) => !outcomes[index].complete && renderItem(row, index))}</>}
    {!!completeCount && <>
      <button type="button" className={classes.assignedToggle} aria-expanded={assignedOpen} aria-controls="assigned-items"
        onClick={() => setAssignedOpen(value => !value)}>✓ Assigned ({completeCount})<ExpandMoreRounded/></button>
      {assignedOpen && <div id="assigned-items">
        {Array.from(completedByPerson, ([key, entries]) => {
          const personIndex = participants.findIndex(person => participantKey(person) === key);
          const person = participants[personIndex];
          const name = nameForKey(key);
          return <section key={key} className={`${classes.person} ${classes.complete}`} aria-label={`${name} completed items`}>
            <div className={classes.personHeading}>
              <span className={`${classes.dot} ${classes.green}`} role="img" aria-label="Fully assigned"/>
              <div><h3>{name}</h3><p>{entries.length} item{entries.length === 1 ? '' : 's'}</p></div>
              {person && avatar(person, personIndex)}
              <strong>{formatCentsAsCurrency(entries.reduce((total, {row}) => total + lineTotalCents(row), 0), currency)}</strong>
            </div>
            {entries.map(({row}) => {
              const label = `${name}${row.quantity !== 1 ? ` ×${row.quantity}` : ''}`;
              return <div key={row.id} className={classes.completedLine}>
                <div><h4>{row.name}</h4><p>{row.quantity} × {formatCurrency(row.price, currency)} each</p></div>
                <strong>{formatCentsAsCurrency(lineTotalCents(row), currency)}</strong>
                <IconButton aria-label={`Remove ${label} from ${row.name}`} title={`Remove ${label} from ${row.name}`}
                  disabled={locked} onClick={() => onChange(row.id, [])}><DeleteOutlineRounded aria-hidden="true"/></IconButton>
              </div>;
            })}
          </section>;
        })}
        {sharedCompleted.map(({row, index}) => renderItem(row, index))}
      </div>}
    </>}
    {guests}
    <h2 className={classes.sectionTitle}>Participants</h2>
    {participants.map((person, personIndex) => {
      const key = participantKey(person);
      const assignedRows = rows.flatMap((row, index) => outcomes[index].perParticipant[key] != null
        ? [{row, amount: outcomes[index].perParticipant[key], shared: row.allocations?.some(portion => portion.participantKeys.includes(key) && portion.participantKeys.length > 1)}] : []);
      const groupedItems = new Map<string, typeof assignedRows[number]>();
      assignedRows.forEach(entry => {
        const signature = JSON.stringify([entry.row.name.trim().toLocaleLowerCase(), entry.row.price]);
        const existing = groupedItems.get(signature);
        if (existing) {
          existing.amount += entry.amount;
          existing.shared = existing.shared || entry.shared;
        } else groupedItems.set(signature, {...entry});
      });
      const assigned = Array.from(groupedItems.values());
      return <section className={classes.person} key={key} aria-label={`${participantName(person)} allocations`}>
        <div className={classes.personHeading}>{avatar(person, personIndex)}<div><h3>{participantName(person)}{!isGuest(person) && person.id === currentUserId && <small>you</small>}{isGuest(person) && <small>Guest</small>}</h3><p>{assigned.length} item{assigned.length === 1 ? '' : 's'}</p></div>
          <strong>{formatCentsAsCurrency(assigned.reduce((sum, entry) => sum + entry.amount, 0), currency)}</strong></div>
        {assigned.map(entry => <div key={entry.row.id} className={classes.personLine}><span>{entry.row.name}{entry.shared && <small>Shared</small>}</span><strong>{formatCentsAsCurrency(entry.amount, currency)}</strong></div>)}
      </section>;
    })}
    {errors}
    <footer className={classes.footer}>
      <div className={classes.progress}><progress aria-label="Assignment progress" value={completeCount} max={rows.length || 1}/><span>{remainingCount ? `${remainingCount} left` : '✓ All assigned'}</span></div>
      <button type="button" className={classes.primary} disabled={!canSave || !!remainingCount || locked} onClick={onSave}>
        {saving ? 'Saving…' : remainingCount ? `Assign ${remainingCount} more item${remainingCount === 1 ? '' : 's'} to continue` : saveLabel}
      </button>
    </footer>
    <Drawer anchor="bottom" open={!!customRow} onClose={() => {setCustomId(undefined); onCustomOpenChange?.(false);}}
      sx={{zIndex: theme => theme.zIndex.modal + 1}}
      ModalProps={{disableEnforceFocus: !!customHelp}}
      PaperProps={{className: classes.sheet, role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'shared-item-title'}}>
      {customRow && <>
        <div className={classes.sheetTitle}><h2 id="shared-item-title">Who shared the {customRow.name}?</h2><IconButton aria-label="Close custom split" onClick={() => {setCustomId(undefined); onCustomOpenChange?.(false);}}><CloseRounded/></IconButton></div>
        {customHelp}
        <div className={classes.quantity}><label htmlFor="shared-quantity">Quantity being shared</label>
          <IconButton aria-label="Decrease shared quantity" disabled={!quantityValid || chosenQuantity <= Math.min(1, remaining)} onClick={() => setQuantity(String(normalizeQuantity(Math.max(Math.min(1, remaining), chosenQuantity - 1))))}><RemoveRounded/></IconButton>
          <input id="shared-quantity" inputMode="decimal" value={quantity} onChange={event => setQuantity(event.target.value)} aria-invalid={!quantityValid}/>
          <IconButton aria-label="Increase shared quantity" disabled={!quantityValid || chosenQuantity >= remaining} onClick={() => setQuantity(String(normalizeQuantity(Math.min(remaining, chosenQuantity + 1))))}><AddRounded/></IconButton>
        </div>
        {!quantityValid && <p role="alert">Enter a quantity greater than zero, up to {remaining} (at most 6 decimal places).</p>}
        <h3 className={classes.sectionTitle}>Select people (min. 2)</h3>
        {participants.map((person, index) => {
          const key = participantKey(person);
          return <button type="button" role="checkbox" aria-checked={selected.includes(key)} className={classes.selectPerson} key={key}
            onClick={() => {setSelected(previous => previous.includes(key) ? previous.filter(value => value !== key) : [...previous, key]); setAdjusting(false);}}>
            {avatar(person, index)}<span className={classes.selectPersonName}>{participantName(person)}{isGuest(person) && <small> · Guest</small>}</span>
            <span className={classes.checkbox} aria-hidden="true">{selected.includes(key) && <CheckRounded/>}</span>
          </button>;
        })}
        {selected.length >= 2 && <>
          <div className={classes.preview}><h3>{adjusting ? 'Custom shares' : 'Equal split'}</h3>
            {selected.map((key, index) => <div key={key}><span>{nameForKey(key)}</span>
              {adjusting && <label><input aria-label={`Percentage for ${nameForKey(key)}`} inputMode="decimal" value={percentages[key] ?? ''}
                onChange={event => setPercentages(previous => ({...previous, [key]: event.target.value}))}/>%</label>}
              <strong>{preview ? formatCentsAsCurrency(preview.shares[index], currency) : '—'}</strong>
            </div>)}
            {adjusting && <p role="status">{Number.isFinite(percentageTotal) ? percentageTotal / 100 : 0}% of 100% assigned</p>}
          </div>
          <button type="button" className={classes.adjust} onClick={() => {
            const base = Math.floor(10000 / selected.length);
            setPercentages(Object.fromEntries(selected.map((key, index) => [key, String((base + (index < 10000 % selected.length ? 1 : 0)) / 100)])));
            setAdjusting(value => !value);
          }}>{adjusting ? 'Use equal shares' : 'Adjust shares…'}</button>
        </>}
        <button type="button" className={classes.primary} disabled={!preview || locked} onClick={() => {
          if (!preview) return;
          onChange(customRow.id, [...(customRow.allocations ?? []), candidate]);
          setCustomId(undefined);
          onCustomOpenChange?.(false);
        }}>Split among {selected.length} people{preview ? ` — ${formatCentsAsCurrency(preview.amountCents, currency)}` : ''}</button>
      </>}
    </Drawer>
  </ReceiptWizardLayout>;
}
